import React, { createContext, useState, useEffect, useContext } from 'react';
import Cookies from 'js-cookie';
import { signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { envConstants } from '../Utils/constants';
import { fetchAccessRoles, fetchEmployees, fetchCompanies } from '../Utils/api';



const AuthContext = createContext();

export function AuthProvider({ children }) {
 
  const [companyId, setCompanyId] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);

  // Extract companyId and user details from the access token on mount
  useEffect(() => {
    const extractUserData = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.payload;
        const accessToken = session.tokens?.accessToken?.payload;
        let accessRole;

        // Company ID is already in the token
        const id = idToken?.['custom:company_id'] || idToken?.company_id;

        if(id){
          setCompanyId(id);
        }

        // Extract basic user details from token
        const email = idToken?.email || idToken?.['cognito:username'] || '';
        const name = idToken?.name || idToken?.given_name || idToken?.['cognito:username'] || 'User';
        const phone = idToken?.phone_number || idToken?.['custom:phone'] || 'Not provided';
        const accessGroups = accessToken?.['cognito:groups']
  

        // Fetch employee details from database to get designation and company name
        let designation = 'Not specified';
        let companyName = 'Not specified';
        let employeeId = null;
        if (id && email) {
          try {
            const employees = await fetchEmployees(id, null, null);
            const roles = await fetchAccessRoles();
            const admin = roles.filter(accessGroup=> accessGroup.role==='Admin')
            const owner = roles.filter(accessGroup=>accessGroup.role==="Owner" || accessGroup.role === 'Co-Owner')

            // Find the current user's employee record by email
            const currentEmployee = employees.find(emp =>
              emp.email?.toLowerCase() === email.toLowerCase()
            );
            if (currentEmployee) {
              designation = currentEmployee.empDesignation || 'Not specified';
              employeeId = currentEmployee.id || null;
            }
            if(admin.some(accessRole =>accessGroups.includes(accessRole.securityGroup))){
              accessRole = 'Admin';
            }else if(owner.some(role=>accessGroups.includes(role.securityGroup))){
              accessRole ='Owner';
             }


            // Fetch company name
            const companies = await fetchCompanies(id);
            if (companies && companies.length > 0) {
              companyName = companies[0].name || 'Not specified';
              setAccountStatus(companies[0].accountStatus || null);
            }

          } catch (error) {
            console.error("Failed to fetch employee designation:", error);
          }
        }

        // Set user details with designation and company name from database
        if (idToken) {
          setUserDetails({
            name,
            email,
            phone,
            designation,
            companyName,
            username: idToken['cognito:username'] || '',
            role: accessRole,
            employeeId,
          });
        }

      } catch (err) {
        console.error("Failed to extract user data from token:", err);
      }
    };
    extractUserData();
  }, []);


  const logout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setCompanyId(null);
      setUserDetails(null);
      setAccountStatus(null);
      Cookies.remove('user');

      // Redirect to Cognito's hosted logout endpoint so the Cognito session
      // is also cleared. We pick the sign-out URL that matches the current origin.
      const signOutUrls = envConstants.AUTH_SIGNOUT_URL;
      const logoutUri =
        signOutUrls.find((url) => url.startsWith(window.location.origin)) ||
        signOutUrls[0];
      const cognitoLogoutUrl =
        `https://${envConstants.AUTH_DOMAIN}/logout` +
        `?client_id=${envConstants.COGNITO_CLIENT_ID}` +
        `&logout_uri=${encodeURIComponent(logoutUri)}`;
      window.location.href = cognitoLogoutUrl;
    }
  };

  const authValue = { companyId, userDetails, accountStatus, logout };
  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook
export function useAuth() {
  return useContext(AuthContext);
}